import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Kbd } from './Badge';
import type { Placement } from './layer';
import { Spinner } from './Spinner';
import { useTooltip } from './Tooltip';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  block?: boolean;
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-press border border-transparent',
  secondary: 'bg-surface text-fg border border-line-strong hover:bg-surface-2 hover:border-line-input active:bg-surface-3',
  ghost: 'bg-transparent text-fg-2 border border-transparent hover:bg-surface-2 hover:text-fg active:bg-surface-3',
  danger: 'bg-danger text-on-accent border border-transparent hover:brightness-95 active:brightness-90',
  'danger-ghost': 'bg-transparent text-danger-fg border border-transparent hover:bg-danger-soft',
  success: 'bg-surface text-success-fg border border-success-line hover:bg-success-soft',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-[var(--control-sm)] px-2.5 gap-1.5 text-meta',
  md: 'h-[var(--control-md)] px-3 gap-2 text-body',
  lg: 'h-[var(--control-lg)] px-4 gap-2 text-body',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, iconRight, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'ers-press inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-sm font-medium select-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 12 : 14} /> : icon}
      {children}
      {iconRight}
    </button>
  );
});

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  size?: ButtonSize;
  variant?: 'ghost' | 'secondary' | 'primary';
  active?: boolean;
  /** Гаряча клавіша для підказки. */
  shortcut?: string;
  tooltipPlacement?: Placement;
};

/** Кнопка лише з іконкою: підпис обов'язковий (aria-label і підказка з гарячою клавішею). */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', variant = 'ghost', active, className, children, type = 'button', shortcut, tooltipPlacement, onPointerEnter, onPointerLeave, onFocus, onBlur, onPointerDown, ...rest },
  ref,
) {
  const dim = size === 'sm' ? 'size-[var(--control-sm)]' : size === 'lg' ? 'size-[var(--control-lg)]' : 'size-[var(--control-md)]';
  const tip = useTooltip(
    <span className="inline-flex items-center gap-1.5">
      {label}
      {shortcut && <Kbd tone="inverse">{shortcut}</Kbd>}
    </span>,
    { describe: false, placement: tooltipPlacement },
  );
  return (
    <>
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-keyshortcuts={shortcut}
      onPointerEnter={(e) => {
        tip.triggerProps.onPointerEnter(e);
        onPointerEnter?.(e);
      }}
      onPointerLeave={(e) => {
        tip.triggerProps.onPointerLeave();
        onPointerLeave?.(e);
      }}
      onPointerDown={(e) => {
        tip.triggerProps.onPointerDown();
        onPointerDown?.(e);
      }}
      onFocus={(e) => {
        tip.triggerProps.onFocus(e);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        tip.triggerProps.onBlur();
        onBlur?.(e);
      }}
      className={cn(
        'ers-press inline-flex shrink-0 items-center justify-center rounded-sm',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'ghost' && 'text-fg-2 hover:bg-surface-2 hover:text-fg active:bg-surface-3',
        variant === 'secondary' && 'border border-line-strong bg-surface text-fg-2 hover:bg-surface-2 hover:text-fg',
        variant === 'primary' && 'bg-accent text-on-accent hover:bg-accent-hover',
        active && 'bg-surface-3 text-fg',
        dim,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
    {tip.element}
    </>
  );
});
